import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import SendIcon from "@mui/icons-material/Send";
import Textarea from "@mui/joy/Textarea";
import { TextField } from "@mui/material";
import PropTypes from "prop-types";
import { ENDPOINT } from "constants/variables";
import { helpHttp } from "service/HelpHttp";
import { setLoading } from "reducers/loadingSlice";
import { LoadingNews } from "components/utils/LoadingNews";
import { successErrorAction, warningErrorAction } from "actions/ErrorActions";
import "css/generalSettings.css";
import "css/customDialog.css";
import ReportTemplateFirst from 'pages/forms/formDISendEmail/ReportTemplateFirst';
import ReportTemplateSecond from 'pages/forms/formDISendEmail/ReportTemplateSecond';
import { addFormDIDecaQrToPdf, fetchFormDIDecaQr } from 'pages/forms/pdf/formDIDecaQr';

/* ----------------------------- helpers/hooks ----------------------------- */
function useDebouncedCallback(fn, delay = 300) {
  const fnRef = useRef(fn);
  const timerRef = useRef(null);
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);
  const debounced = useCallback(
    (...args) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fnRef.current(...args), delay);
    },
    [delay]
  );
  // cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  return debounced;
}

function useObjectUrl(blob) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!blob) {
      setUrl("");
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [blob]);

  return url;
}

function sanitizeEmailQuery(value) {
  // Mantengo tu intención: quitar caracteres raros que pueden romper endpoint
  // (esto NO valida email, solo sanea la query).
  return (value ?? "").replace(/[.;\-/\\|]/g, "").trim();
}

async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== "string") return reject(new Error("base64-fail"));
      resolve(result.split(",")[1] ?? "");
    };
    reader.onerror = reject;
  });
}

/* ------------------------------ initial state ---------------------------- */
const initialStateFromDISendEmail = {
  ownformdi_id:0,
  defaultStore_id: 0,
  useraccount_id: 0, 
  defaultformatnumber: 1,
  isFinished: true,
  isScanned: false,
  numberDI_sheet: "", 
  date_sheet: "",
 
  opt_tras_documnettype: "",
  opt_tras_documentid: "",
  opt_tras_name: "",
  opt_tras_contactname: "", 
  opt_tras_cnae: "",
  opt_tras_nima: "",
  opt_tras_country: "",
  opt_tras_province: "",
  opt_tras_locality: "",
  opt_tras_community: "",
  opt_tras_phone: "",
  opt_tras_licenseplate: "",
  opt_tras_economicactivity: "",
  opt_tras_tradename: "",
  opt_tras_street: "",
  opt_tras_postalcode: "",
  opt_tras_email: "",
  opt_tras_inscription: "",
  opt_tras_opt: "",
  
  ori_tras_documnettype: "",
  ori_tras_documentid: "",
  ori_tras_name: "",
  ori_tras_contactname: "", 
  ori_tras_cnae: "",
  ori_tras_nima: "",
  ori_tras_country: "",
  ori_tras_province: "",
  ori_tras_locality: "",
  ori_tras_community: "",
  ori_tras_phone: "",
  ori_tras_licenseplate: "",
  ori_tras_economicactivity: "",
  ori_tras_tradename: "",
  ori_tras_street: "",
  ori_tras_postalcode: "",
  ori_tras_email: "",
  ori_tras_inscription: "",
  ori_tras_opt: "",

  aut_tras_documnettype: "",
  aut_tras_documentid: "",
  aut_tras_name: "",
  aut_tras_contactname: "", 
  aut_tras_cnae: "",
  aut_tras_nima: "",
  aut_tras_country: "",
  aut_tras_province: "",
  aut_tras_locality: "",
  aut_tras_community: "",
  aut_tras_phone: "",
  aut_tras_licenseplate: "",
  aut_tras_economicactivity: "",
  aut_tras_tradename: "",
  aut_tras_street: "",
  aut_tras_postalcode: "",
  aut_tras_email: "",
  aut_tras_inscription: "",
  aut_tras_opt: "",
 
  dest_tras_documnettype: "",
  dest_tras_documentid: "",
  dest_tras_name: "",
  dest_tras_contactname: "", 
  dest_tras_cnae: "",
  dest_tras_nima: "",
  dest_tras_country: "",
  dest_tras_province: "",
  dest_tras_locality: "",
  dest_tras_community: "",
  dest_tras_phone: "",
  dest_tras_licenseplate: "",
  dest_tras_economicactivity: "",
  dest_tras_tradename: "",
  dest_tras_street: "",
  dest_tras_postalcode: "",
  dest_tras_email: "",
  dest_tras_inscription: "",
  dest_tras_opt: "",
  
  aut_dest_documnettype: "",
  aut_dest_documentid: "",
  aut_dest_name: "",
  aut_dest_contactname: "", 
  aut_dest_cnae: "",
  aut_dest_nima: "",
  aut_dest_country: "",
  aut_dest_province: "",
  aut_dest_locality: "",
  aut_dest_community: "",
  aut_dest_phone: "",
  aut_dest_licenseplate: "",
  aut_dest_economicactivity: "",
  aut_dest_tradename: "",
  aut_dest_street: "",
  aut_dest_postalcode: "",
  aut_dest_email: "",
  aut_dest_inscription: "",
  aut_dest_opt: "",
  
  rel_trans_documnettype: "",
  rel_trans_documentid: "",
  rel_trans_name: "",
  rel_trans_contactname: "", 
  rel_trans_cnae: "",
  rel_trans_nima: "",
  rel_trans_country: "",
  rel_trans_province: "",
  rel_trans_locality: "",
  rel_trans_community: "",
  rel_trans_phone: "",
  rel_trans_licenseplate: "",
  rel_trans_economicactivity: "",
  rel_trans_tradename: "",
  rel_trans_street: "",
  rel_trans_postalcode: "",
  rel_trans_email: "",
  rel_trans_inscription: "",
  rel_trans_opt: "",
 
  resp_prod_documnettype: "",
  resp_prod_documentid: "",
  resp_prod_name: "",
  resp_prod_contactname: "", 
  resp_prod_cnae: "",
  resp_prod_nima: "",
  resp_prod_country: "",
  resp_prod_province: "",
  resp_prod_locality: "",
  resp_prod_community: "",
  resp_prod_phone: "",
  resp_prod_licenseplate: "",
  resp_prod_economicactivity: "",
  resp_prod_tradename: "",
  resp_prod_street: "",
  resp_prod_postalcode: "",
  resp_prod_email: "",
  resp_prod_inscription: "",
  resp_prod_opt: "",

  codeLER: "",
  codeLER_code: "",
  codeLER_name: "",
  description: "",
  description_code: "",
  description_name: "",
  treatmenttypeorigin_code: "",
  treatmenttypeorigin_name: "",
  treatmenttypedestine_code: "",
  treatmenttypedestine_name: "",
  processtypepeorigin_code: "",
  processtypepeorigin_name: "",
  processtypedestine_code: "",
  processtypedestine_name: "",
  dangerousness_name: "",
  charac_danger: "",
  amount: "",
  deliver_date: "",
  delivery_quantity: "",
  date_accepted: "",
  accept: "",
  date_refund: "",
  reason_refund: "",
  action_refund: "",
  observations1: "",
  observations2: "" ,
  
  signature_opt_tras:"",
  signature_dest_tras:"",
  signature_refund:""  ,
  end_date_sheet:"",
  widevalidity:false 
}; 

const initialStateEMAIL = {
  id: "",
  defaultStore_id: 0,
  useraccount_id: 0,
  store_EmailTo: "",
  subjectEmail: "",
  contentEmail: "",
  filetosend: [],
};
 
const FormDISendEmail = ({id, showModalFormDISendEmail,typeOfList}) => {
  const { t, i18n } = useTranslation(["listDataForms"]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state.loginUser.token);   
  const defaultStore = useSelector((state) => state.loginUser.defaultStore)
  const useraccount_id = useSelector((state) => state.loginUser.useraccount_id);  
  const footeremail = useSelector((state) => state.formatDIUser.footeremail); 
  const defaulttypesdi = useSelector((state) => state.loginUser.defaulttypesdi.formatnumber);  
  const sendbyemail_form_opt_tras = useSelector((state) => state.formatDIUser.sendbyemail_form_opt_tras);
  const sendbyemail_form_ori_tras = useSelector((state) => state.formatDIUser.sendbyemail_form_ori_tras);
  const sendbyemail_form_aut_tras = useSelector((state) => state.formatDIUser.sendbyemail_form_aut_tras);
  const sendbyemail_form_aut_dest = useSelector((state) => state.formatDIUser.sendbyemail_form_aut_dest);
  const sendbyemail_form_dest_tras = useSelector((state) => state.formatDIUser.sendbyemail_form_dest_tras);
  const sendbyemail_form_rel_trans = useSelector((state) => state.formatDIUser.sendbyemail_form_rel_trans);
  const sendbyemail_form_resp_prod = useSelector((state) => state.formatDIUser.sendbyemail_form_resp_prod);

  const isLoadingData = useSelector((state) => state.loading.isLoading);
  const [dataForm, setDataForm] = useState(initialStateFromDISendEmail);
  const [formEMAIL, setFormEMAIL] = useState(initialStateEMAIL);
  const [errorsEMAIL, setErrorsEMAIL] = useState({});
  const [pdfBlob, setPdfBlob] = useState(null);       // generado por ReportTemplate
  const [pdfS3Blob, setPdfS3Blob] = useState(null);   // descargado si isScanned
  const [emailOptions, setEmailOptions] = useState([]); // opciones devueltas por API
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndexEmail, setHighlightedIndexEmail] = useState(-1);
  const dropdownRef = useRef(null);
  const pdfBlobUrl = useObjectUrl(pdfBlob);
  const pdfS3Url = useObjectUrl(pdfS3Blob);
  const pdfSrc = pdfS3Url || pdfBlobUrl;

  const PdfViewer = useMemo(() => {
    if (!pdfSrc) return null;
    return (
      <iframe
        src={pdfSrc}
        title="PDF Preview"
        style={{ width: "100%", height: "100%", border: "none" }}
      />
    );
  }, [pdfSrc]);

  /* ------------------------------- utilities ------------------------------ */

  const resetForm = useCallback(() => {
    setDataForm(initialStateFromDISendEmail);
    setFormEMAIL(initialStateEMAIL);
    setErrorsEMAIL({});
    setEmailOptions([]);
    setIsDropdownOpen(false);
    setHighlightedIndexEmail(-1);
    setPdfBlob(null);
    setPdfS3Blob(null);
    dispatch(setLoading(false));
  }, [dispatch]);

  const goBack = useCallback(() => {
    navigate(typeOfList === "1" ? "/formdi" : "/formdiended");
  }, [navigate, typeOfList]);

  const handleClose = useCallback(() => {
    resetForm();
    goBack();
  }, [resetForm, goBack]);

  const scrollToElement = useCallback((index) => {
    const dropdown = dropdownRef.current;
    const item = dropdown?.children?.[index];
    if (item && typeof item.scrollIntoView === "function") {
      item.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, []);

  const validateFormEmail = useCallback(() => {
    const errors = {};
    const regexInvalidCharacters = /[\\!\\$\\%\\&\\|\\<\\>\\?\\{\\}\\~\\"\\']+/;
   
    if (!formEMAIL.store_EmailTo || formEMAIL.store_EmailTo === "") {
      errors.store_EmailTo = "Text_FieldSendEmailToRequired";
    } else if (regexInvalidCharacters.test(formEMAIL.store_EmailTo)) {
      errors.store_EmailTo = "Text_TextNoValid";
    }

    if (regexInvalidCharacters.test(formEMAIL.subjectEmail)) {
      errors.subjectEmail = "Text_TextNoValid";
    }

    if (regexInvalidCharacters.test(formEMAIL.contentEmail)) {
      errors.contentEmail = "Text_TextNoValid";
    }

    return errors;
  }, [formEMAIL.store_EmailTo, formEMAIL.subjectEmail, formEMAIL.contentEmail]);

  const buildFileName = useCallback(() => {
    const sheet = dataForm.numberDI_sheet;
    if (sheet !== undefined && sheet !== "" && sheet !== 0) return `FORMDI_${sheet}.pdf`;
    return `FORMDI_${id.toString().padStart(8, "0")}.pdf`;
  }, [dataForm.numberDI_sheet, id]);

  /* ---------------------------- API: send email --------------------------- */

  const handleConfirmFetchSendFormDI = useCallback(
    async (payload, data_id) => {
      // cierro inmediatamente para mantener tu UX original
      handleClose();

      try {
        const getData = await helpHttp().postemailformdi(
          ENDPOINT,
          "api/sendemailwithattachmentformdi",
          token,
          payload,
          data_id
        );

        if (getData?.status === "OK") {
          dispatch(setLoading(false));
          dispatch(successErrorAction(t("Text_EMAIL_Sended")));
        } else {
          dispatch(setLoading(false));
          dispatch(warningErrorAction(t(getData?.cause ?? "Text_IncorrectData")));
        }
      } catch {
        dispatch(setLoading(false));
        dispatch(warningErrorAction(t("Text_IncorrectData")));
      }
    },
    [dispatch, handleClose, t, token]
  );

  const handleSubmitSendEmail = useCallback(async () => {
    const errors = validateFormEmail();
    setErrorsEMAIL(errors);

    const hasNoErrors = Object.keys(errors).length === 0;
    const emailToOk = formEMAIL?.store_EmailTo?.trim();
    const hasPdf = !!pdfBlob || !!pdfS3Blob;
    if (!hasNoErrors || !emailToOk || !hasPdf) {
      if (errors.store_EmailTo) dispatch(warningErrorAction(t(errors.store_EmailTo)));
      else if (errors.subjectEmail) dispatch(warningErrorAction(t(errors.subjectEmail)));
      else if (errors.contentEmail) dispatch(warningErrorAction(t(errors.contentEmail)));
      else dispatch(warningErrorAction(t("Text_IncorrectData")));
      return;
    }
    dispatch(setLoading(true));
    try {
      const chosenBlob = pdfBlob || pdfS3Blob;
      if (!chosenBlob) throw new Error("no-pdf");
      const base64File = await blobToBase64(chosenBlob);
      const payload = {
        id,
        defaultStore_id: defaultStore.id,
        useraccount_id,
        store_EmailTo: formEMAIL.store_EmailTo,
        subjectEmail: formEMAIL.subjectEmail,
        contentEmail: formEMAIL.contentEmail,
        filetosend: base64File,
        fileName: buildFileName(),
        fileType: "application/pdf",
      };
      await handleConfirmFetchSendFormDI(payload, dataForm.id);
    } catch {
      dispatch(setLoading(false));
      dispatch(warningErrorAction(t("Text_IncorrectData")));
    }
  }, [
    validateFormEmail,
    formEMAIL,
    pdfBlob,
    pdfS3Blob,
    dispatch,
    t,
    id,
    defaultStore.id,
    useraccount_id,
    buildFileName,
    handleConfirmFetchSendFormDI,
    dataForm.id,
  ]);

  /* ------------------------ input handlers: email ------------------------- */

  const handleChangeDataEditEmail = useCallback((e) => {
    const { name, value } = e.target;
    setFormEMAIL((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleBlurDataEditEmail = useCallback(
    (e) => {
      handleChangeDataEditEmail(e);
      setErrorsEMAIL(validateFormEmail());
    },
    [handleChangeDataEditEmail, validateFormEmail]
  );

  const handleSelectEmail = useCallback((option) => {
    const email = option?.email ?? "";
    if (!email) return;

    setFormEMAIL((prev) => ({ ...prev, store_EmailTo: email }));
    setIsDropdownOpen(false);
    setHighlightedIndexEmail(-1);
  }, []);

  const handleKeyDownEmail = useCallback(
    (event) => {
      if (!isDropdownOpen && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
        if (emailOptions.length) {
          setIsDropdownOpen(true);
          setHighlightedIndexEmail(0);
        }
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndexEmail((prev) => {
          const next = Math.min(prev + 1, emailOptions.length - 1);
          scrollToElement(next);
          return next;
        });
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndexEmail((prev) => {
          const next = Math.max(prev - 1, 0);
          scrollToElement(next);
          return next;
        });
      } else if ((event.key === "Enter" || event.key === "Tab") && highlightedIndexEmail >= 0) {
        if (highlightedIndexEmail < emailOptions.length) {
          event.preventDefault();
          handleSelectEmail(emailOptions[highlightedIndexEmail]);
        }
      } else if (event.key === "Escape") {
        setIsDropdownOpen(false);
        setHighlightedIndexEmail(-1);
      }
    },
    [emailOptions, highlightedIndexEmail, handleSelectEmail, isDropdownOpen, scrollToElement]
  );

  const closeDropdownOnBlur = useCallback((event) => {
    // Cierro el dropdown si el focus se va fuera del wrapper y fuera del dropdown
    // (evita que se cierre al clicar una opción).
    const related = event?.relatedTarget;
    const relatedInDropdown = related?.closest?.(".email-suggest-dropdown");
    if (!relatedInDropdown) {
      setIsDropdownOpen(false);
      setHighlightedIndexEmail(-1);
    }
  }, []);

  /* ------------------------ API: email suggestions ------------------------ */
  const fetchEmailOptions = useCallback(
    async (rawValue) => {
      const q = sanitizeEmailQuery(rawValue);
      if (!showModalFormDISendEmail || !q) {
        setEmailOptions([]);
        setIsDropdownOpen(false);
        setHighlightedIndexEmail(-1);
        return;
      }

      try {
        const getData = await helpHttp().get2(
          ENDPOINT,
          "api/getlistemails/",
          token,
          q !== "" ? q : "-1"
        );

        if (getData?.err) {
          setEmailOptions([]);
          setIsDropdownOpen(false);
          setHighlightedIndexEmail(-1);
          dispatch(warningErrorAction(t("Text_InvalidCredentials")));
          return;
        }

        const list = Array.isArray(getData) ? getData.filter((x) => x?.email) : [];
        setEmailOptions(list);
        setIsDropdownOpen(list.length > 0);
        setHighlightedIndexEmail(list.length ? 0 : -1);
      } catch {
        setEmailOptions([]);
        setIsDropdownOpen(false);
        setHighlightedIndexEmail(-1);
        dispatch(warningErrorAction(t("Text_IncorrectData")));
      }
    },
    [dispatch, showModalFormDISendEmail, t, token]
  );

  const debouncedFetchEmailOptions = useDebouncedCallback(fetchEmailOptions, 250);

  /* -------------------------- effect: Escape key -------------------------- */
  useEffect(() => {
    if (!showModalFormDISendEmail) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [showModalFormDISendEmail, handleClose]);

  /* ------------------------- effect: load DI data ------------------------- */

  useEffect(() => {
    let alive = true;
    const loadFormDI = async () => {
      if (!showModalFormDISendEmail) return;
      // id "0" => modo "nuevo" según tu lógica original
      if (id === "0") {
        setDataForm((prev) => ({ ...initialStateFromDISendEmail, linespurchasedelivery: [] }));
        setFormEMAIL((prev) => ({
          ...prev,
          defaultStore_id: defaultStore.id,
          useraccount_id,
          contentEmail: footeremail,
        }));
        dispatch(setLoading(false));
        return;
      }
      dispatch(setLoading(true));
      try {
        const getData = await helpHttp().get8(
          ENDPOINT,
          "form/getpdfformdibyid",
          token,
          id,
          defaulttypesdi
        );
        if (!alive) return;
        if (getData?.err) {
          setDataForm(initialStateFromDISendEmail);
          dispatch(setLoading(false));
          dispatch(
            warningErrorAction(
              t(getData.message === "Load failed" ? "Text_ConnectionFailied" : "Text_InvalidCredentials")
            )
          );
          return;
        }
        const decaQrDataUrl = await fetchFormDIDecaQr(getData?.id || id, token);
        if (!alive) return;
        setDataForm({ ...getData, decaQrDataUrl });
        // emails por flags (con deduplicación)
        const collected = [
          sendbyemail_form_opt_tras ? getData.opt_tras_email : "",
          sendbyemail_form_ori_tras ? getData.ori_tras_email : "",
          sendbyemail_form_aut_tras ? getData.aut_tras_email : "",
          sendbyemail_form_aut_dest ? getData.aut_dest_email : "",
          sendbyemail_form_dest_tras ? getData.dest_tras_email : "",
          sendbyemail_form_rel_trans ? getData.rel_trans_email : "",
          sendbyemail_form_resp_prod ? getData.resp_prod_email : "",
        ]
          .filter((e) => e && String(e).trim() !== "")
          .map((e) => String(e).trim().toLowerCase());

        const uniqueEmails = [...new Set(collected)].join(";");
        setFormEMAIL((prev) => ({
          ...prev,
          defaultStore_id: defaultStore.id,
          useraccount_id,
          store_EmailTo: uniqueEmails,
          subjectEmail: "LE REMITO EL FORMULARIO D.I. Nº: " + (getData.numberDI_sheet ?? ""),
          contentEmail: footeremail,
        }));
        // si escaneado, el PDF vendrá de S3 => limpiamos pdfBlob generado
        if (getData?.isScanned) {
          setPdfBlob(null);
        } else {
          setPdfS3Blob(null);
        }
        dispatch(setLoading(false));
      } catch {
        if (!alive) return;
        dispatch(setLoading(false));
        dispatch(warningErrorAction(t("Text_IncorrectData")));
      }
    };
    loadFormDI();
    return () => {
      alive = false;
    };
  }, [
    showModalFormDISendEmail,
    id,
    token,
    defaulttypesdi,
    dispatch,
    t,
    defaultStore.id,
    useraccount_id,
    footeremail,
    sendbyemail_form_opt_tras,
    sendbyemail_form_ori_tras,
    sendbyemail_form_aut_tras,
    sendbyemail_form_aut_dest,
    sendbyemail_form_dest_tras,
    sendbyemail_form_rel_trans,
    sendbyemail_form_resp_prod,
  ]);

  /* ---------------------- effect: download scanned PDF --------------------- */

  useEffect(() => {
    let alive = true;
    const fetchScannedPdf = async () => {
      if (!showModalFormDISendEmail) return;
      if (!dataForm?.isScanned) return;
      const sheet = dataForm.numberDI_sheet;
      if (!sheet) return;
      try {
        const fileName = `DI_${sheet}.pdf`;
        const blob = await helpHttp().getfilesupload(
          ENDPOINT,
          "pdftos3uploader/downloadfroms3",
          token,
          fileName
        );
        if (!alive) return;
        if (!blob || !blob.size) {
          setPdfS3Blob(null);
          dispatch(warningErrorAction(t("Text_ConnectionFailed")));
          return;
        }
        setPdfS3Blob(await addFormDIDecaQrToPdf(blob, dataForm?.decaQrDataUrl));
      } catch {
        if (!alive) return;
        setPdfS3Blob(null);
        dispatch(warningErrorAction(t("Text_IncorrectData")));
      }
    };
    fetchScannedPdf();
    return () => {
      alive = false;
    };
  }, [showModalFormDISendEmail, dataForm?.isScanned, dataForm?.numberDI_sheet, dataForm?.decaQrDataUrl, token, dispatch, t]);

  /* ------------------------------- render -------------------------------- */

  return (
    <>
      {showModalFormDISendEmail ? (
        <div className="container-fluid email-send-wrapper">
          <Box sx={{ bgcolor: "#ffffff", width: "1050px" }}>
            <form onSubmit={(e) => e.preventDefault()}>
              {/* HEADER */}
              <div className="email-card-header">
                <div className="email-header-content">
                  <div className="email-header-icon">
                    <i className="fas fa-envelope"></i>
                  </div>

                  <div className="email-header-text">
                    <div className="email-header-title">{t("Text_NumberForm_EMAIL")}:</div>
                    <div className="email-header-number">{dataForm.numberDI_sheet}</div>
                  </div>

                  <div className="email-card-actions">
                    {!isLoadingData && (
                      <>
                        <button type="button" className="email-send-btn" onClick={handleSubmitSendEmail}>
                          {t("Button_SEND_ESIR")}
                          <SendIcon sx={{ fontSize: 22, color: "white" }} />
                        </button>
                        <button type="button" className="email-cancel-btn" onClick={handleClose}>
                          {t("Button_Cancel")}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* BODY */}
              {!isLoadingData ? (
                <>
                  {/* FORM CARD */}
                  <div className="email-card-form">
                    <div className="row g-3">
                      {/* Email To */}
                      <div className="col-12 email-input" style={{ position: "relative" }}>
                        <label className="text-primary" style={{ fontSize: 18 }}>
                          {t("Text_SENDEMAIL_EmailsTo")} :
                        </label>

                        <TextField
                          id="store_EmailTo"
                          name="store_EmailTo"
                          value={formEMAIL.store_EmailTo}
                          onChange={(e) => {
                            const v = (e.target.value ?? "").toLowerCase();
                            setFormEMAIL((prev) => ({ ...prev, store_EmailTo: v }));
                            debouncedFetchEmailOptions(v);
                          }}
                          onKeyDown={handleKeyDownEmail}
                          onBlur={closeDropdownOnBlur}
                          label={
                            errorsEMAIL.store_EmailTo != null
                              ? t(errorsEMAIL.store_EmailTo)
                              : t("PlaceHolder_SENDEMAIL_EmailsTo")
                          }
                          placeholder={t("PlaceHolder_SENDEMAIL_EmailsTo")}
                          variant="standard"
                          inputProps={{
                            style: { width: i18n.language === "es" ? 738 : 867 },
                            maxLength: 100,
                          }}
                          InputLabelProps={{ style: { fontSize: 18, background: "#FFFFFF" } }}
                        />

                        {isDropdownOpen && emailOptions.length > 0 && (
                          <div
                            ref={dropdownRef}
                            className="email-suggest-dropdown"
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {emailOptions.slice(0, 20).map((opt, index) => (
                              <MenuItem
                                key={opt.id ?? `${opt.email}-${index}`}
                                tabIndex={-1}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleSelectEmail(opt)}
                                style={{
                                  cursor: "pointer",
                                  padding: "10px 12px",
                                  borderBottom: "1px solid #eee",
                                  fontSize: 18,
                                  backgroundColor: index === highlightedIndexEmail ? "#4e73df" : "transparent",
                                  color: index === highlightedIndexEmail ? "white" : "black",
                                }}
                              >
                                {opt.email}
                              </MenuItem>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Subject */}
                      <div className="col-12 email-input">
                        <label className="text-primary" style={{ fontSize: 18 }}>
                          {t("Text_SENDEMAIL_Subject")}
                        </label>

                        <TextField
                          id="subjectEmail"
                          name="subjectEmail"
                          value={formEMAIL.subjectEmail ?? ""}
                          inputProps={{
                            style: { width: i18n.language === "es" ? 941 : 937 },
                            maxLength: 100,
                          }}
                          InputLabelProps={{ style: { fontSize: 18, background: "#FFFFFF" } }}
                          label={
                            errorsEMAIL.subjectEmail != null
                              ? t(errorsEMAIL.subjectEmail)
                              : t("PlaceHolder_SENDEMAIL_Subject")
                          }
                          placeholder={t("PlaceHolder_SENDEMAIL_Subject")}
                          variant="standard"
                          onBlur={handleBlurDataEditEmail}
                          onChange={handleChangeDataEditEmail}
                        />
                      </div>

                      {/* Content */}
                      <div className="col-12">
                        <label className="text-primary" style={{ fontSize: 18 }}>
                          {t("Text_SENDEMAIL_Content")} :
                        </label>

                        <Textarea
                          id="contentEmail"
                          name="contentEmail"
                          value={formEMAIL.contentEmail}
                          className="email-textarea"
                          label={
                            errorsEMAIL.contentEmail != null
                              ? t(errorsEMAIL.contentEmail)
                              : t("PlaceHolder_SENDEMAIL_Content")
                          }
                          placeholder={t("PlaceHolder_SENDEMAIL_Content")}
                          sx={{ width: 1000, fontSize: 18 }}
                          variant="standard"
                          onBlur={handleBlurDataEditEmail}
                          onChange={handleChangeDataEditEmail}
                          maxRows={8}
                        />
                      </div>
                    </div>
                  </div>

                  {/* PDF CARD */}
                  <div className="email-card-pdf">{PdfViewer}</div>
                </>
              ) : (
                <LoadingNews />
              )}
            </form>
          </Box>

          {/* Generación de PDF solo si NO viene escaneado (S3) */}
          {!pdfS3Blob && !isLoadingData && dataForm.id !== 0 && !dataForm.isScanned && (
            defaulttypesdi === 1 ? (
              <ReportTemplateFirst dataForm={dataForm} setPdfBlob={setPdfBlob} />
            ) : (
              <ReportTemplateSecond dataForm={dataForm} setPdfBlob={setPdfBlob} />
            )
          )}

          <br />
        </div>
      ) : null}
    </>
  );
};

FormDISendEmail.propTypes = {
  id: PropTypes.string.isRequired,
  showModalFormDISendEmail: PropTypes.bool.isRequired,
  typeOfList: PropTypes.string,
};

export default FormDISendEmail;
